import { FC } from 'react';
import classNames from 'classnames';
import { SparklesIcon } from '@heroicons/react/outline';

import { MailSequenceEnrollmentActivityDataFragment } from '../../../../generated/graphql';

import ContentPreview from '../../../components/ContentPreview';
import { ActivityOrResourceOptions, ContentType } from '../../../lib/followUp';
import { parseMarkdown } from '../../../lib/markdown';

const ACTIVITY_TYPE_TO_CONTENT: Partial<Record<string, ContentType>> = {
  Assessment: ContentType.Assessment,
  Action: ContentType.Action,
  Link: ContentType.Link,
  PDF: ContentType.PDF,
  Audio: ContentType.Audio,
  Image: ContentType.Image,
  Text: ContentType.Text,
};

interface Props {
  activity: MailSequenceEnrollmentActivityDataFragment;
  className?: string;
}

const EnrollmentActivityCard: FC<Props> = ({ activity, className }) => {
  // Custom forms share activityType "Assessment" but use the purple Form icon.
  const contentType =
    activity.activityType === 'Assessment' &&
    activity.subtitle === 'Custom form'
      ? ContentType.Form
      : ACTIVITY_TYPE_TO_CONTENT[activity.activityType] ?? ContentType.Action;

  return (
    <div
      className={classNames(
        'w-full rounded-lg border border-neutral-75 bg-white p-4',
        className,
      )}
    >
      {activity.signerCount > 0 && (
        <div className="mb-4 flex w-full flex-row items-start justify-start rounded-md bg-neutral-25 p-4">
          <SparklesIcon className="mr-2 h-5 w-5 text-neutral-125" />
          <div className="w-full text-caption text-neutral-125">
            Provider signs first on this form.
          </div>
        </div>
      )}

      <ContentPreview
        className="rounded-b-none border-b border-neutral-50 pb-4 shadow-none"
        contentType={contentType}
        title={activity.title}
        description={activity.subtitle ?? undefined}
        activityOrResourceOption={ActivityOrResourceOptions.Resource}
        disableClick
        toggleActivityOrResourceLabel={false}
      />

      {activity.providerNote && (
        <div
          className="prose prose-sm mt-4 max-w-none break-words text-caption text-neutral-125"
          dangerouslySetInnerHTML={{
            __html: parseMarkdown(activity.providerNote),
          }}
        />
      )}

      {!activity.isAvailable && activity.unavailableReason && (
        <p className="text-yellow-150 mt-3 text-small-caption">
          {activity.unavailableReason} This mail will be skipped when it comes
          due.
        </p>
      )}
    </div>
  );
};

export default EnrollmentActivityCard;
